import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../api/axios';

/**
 * Drill-down de um usuário: aba Info / Códigos / Créditos / Atividade
 * Usa endpoint GET /admin/users/:id/details
 */
const AdminUserDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('info');

  // Modais
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjustMode, setAdjustMode] = useState('add');
  const [adjustQty, setAdjustQty] = useState('');
  const [adjustReason, setAdjustReason] = useState('');
  const [adjustError, setAdjustError] = useState('');
  const [adjustSuccess, setAdjustSuccess] = useState('');
  const [adjustLoading, setAdjustLoading] = useState(false);

  const fetchDetails = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await api.get(`/admin/users/${id}/details`);
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Erro ao carregar usuário');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchDetails(); }, [fetchDetails]);

  const formatDateTime = (s) => {
    if (!s) return '—';
    const d = new Date(s.includes('T') ? s : s.replace(' ', 'T'));
    if (isNaN(d.getTime())) return s;
    return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };
  const formatRel = (s) => {
    if (!s) return '';
    const d = new Date(s.includes('T') ? s : s.replace(' ', 'T'));
    if (isNaN(d.getTime())) return '';
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return 'há instantes';
    if (diff < 3600) return `há ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `há ${Math.floor(diff / 3600)} h`;
    return `há ${Math.floor(diff / 86400)} d`;
  };

  // ============ Adjust credits ============
  const openAdjust = (mode) => {
    setAdjustMode(mode);
    setAdjustQty('');
    setAdjustReason('');
    setAdjustError('');
    setAdjustSuccess('');
    setAdjustOpen(true);
  };
  const submitAdjust = async () => {
    setAdjustError('');
    setAdjustSuccess('');
    const qty = parseInt(adjustQty);
    if (isNaN(qty) || qty <= 0) {
      setAdjustError('Informe uma quantidade válida (> 0)');
      return;
    }
    if (qty > 100000) {
      setAdjustError('Limite máximo: 100.000');
      return;
    }
    if (adjustMode === 'remove' && qty > data.user.credits) {
      setAdjustError(`Saldo insuficiente. Usuário tem ${data.user.credits} créditos.`);
      return;
    }
    setAdjustLoading(true);
    try {
      const delta = adjustMode === 'add' ? qty : -qty;
      const r = await api.post(`/admin/users/${id}/credits/adjust`, {
        delta, reason: adjustReason.trim() || null,
      });
      setAdjustSuccess(`OK! Novo saldo: ${r.data.newCredits} créditos`);
      await fetchDetails();
      setTimeout(() => setAdjustOpen(false), 1200);
    } catch (err) {
      setAdjustError(err.response?.data?.error || 'Erro ao ajustar créditos');
    } finally {
      setAdjustLoading(false);
    }
  };

  if (loading) {
    return (
      <>
        <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0-beta3/css/all.min.css" />
        <link href="https://fonts.googleapis.com/css2?family=Lexend:wght@100..900&display=swap" rel="stylesheet" />
        <style>{`
          .aud-loading { min-height: 100vh; background: #f5f5f5; display: flex; flex-direction: column; gap: 12px; align-items: center; justify-content: center; font-family: 'Lexend', sans-serif; color: #666; }
          .aud-spinner { width: 36px; height: 36px; border: 3px solid rgba(255,153,0,0.2); border-top-color: #f90; border-radius: 50%; animation: aud-spin 0.8s linear infinite; }
          @keyframes aud-spin { to { transform: rotate(360deg); } }
        `}</style>
        <div className="aud-loading"><div className="aud-spinner"></div><p>Carregando usuário...</p></div>
      </>
    );
  }

  if (error || !data) {
    return (
      <>
        <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0-beta3/css/all.min.css" />
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5', flexDirection: 'column', gap: 16, fontFamily: 'sans-serif' }}>
          <i className="fas fa-exclamation-triangle" style={{ fontSize: 48, color: '#e53e3e' }}></i>
          <p style={{ color: '#c53030' }}>{error || 'Usuário não encontrado'}</p>
          <button onClick={() => navigate('/admin/users')} style={{ padding: '10px 20px', background: '#f90', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer' }}>Voltar</button>
        </div>
      </>
    );
  }

  const { user, summary, codes, totalCodes, orders, activity, logins } = data;

  return (
    <>
      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0-beta3/css/all.min.css" />
      <link href="https://fonts.googleapis.com/css2?family=Lexend:wght@100..900&display=swap" rel="stylesheet" />
      <style>{`
        .aud *, .aud *::before, .aud *::after { margin: 0; padding: 0; box-sizing: border-box; }
        .aud { font-family: 'Lexend', sans-serif; color: #555; background: #f5f5f5; min-height: 100vh; -webkit-font-smoothing: antialiased; }

        .aud-header {
          background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
          padding: 18px 30px; display: flex; align-items: center; gap: 20px;
          box-shadow: 0 4px 25px rgba(0,0,0,0.15); position: sticky; top: 0; z-index: 1000;
        }
        .aud-header-title { font-size: 20px; font-weight: 700; color: #fff; flex: 1; }
        .aud-header-back {
          display: flex; align-items: center; gap: 8px; color: #f90;
          cursor: pointer; transition: all 0.3s; font-weight: 500;
        }
        .aud-header-back:hover { color: #fff; transform: translateX(-3px); }

        .aud-main { max-width: 1200px; margin: 0 auto; padding: 30px 20px; }

        /* User card hero */
        .aud-hero {
          background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f172a 100%);
          border-radius: 20px; padding: 30px; margin-bottom: 24px;
          border: 1px solid rgba(255,153,0,0.20);
          box-shadow: 0 10px 40px rgba(0,0,0,0.20);
          position: relative; overflow: hidden;
          display: flex; align-items: center; gap: 24px;
        }
        .aud-hero::before {
          content: ''; position: absolute; top: -50%; right: -10%;
          width: 360px; height: 360px;
          background: radial-gradient(circle, rgba(255,153,0,0.18), transparent 70%);
          filter: blur(40px); pointer-events: none;
        }
        .aud-hero-avatar {
          width: 100px; height: 100px; border-radius: 50%;
          background: linear-gradient(135deg, #f90, #ff6600);
          color: #fff; display: flex; align-items: center; justify-content: center;
          font-weight: 800; font-size: 42px; flex-shrink: 0; position: relative; z-index: 1;
          box-shadow: 0 10px 28px rgba(255,153,0,0.40), inset 0 1px 0 rgba(255,255,255,0.30);
          border: 3px solid rgba(255,255,255,0.10);
        }
        .aud-hero-info { flex: 1; position: relative; z-index: 1; min-width: 0; }
        .aud-hero-info h1 {
          font-size: 32px; font-weight: 800; color: #fff; margin-bottom: 4px;
          background: linear-gradient(135deg, #fff 0%, #fdba74 100%);
          -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent;
        }
        .aud-hero-email { color: rgba(255,255,255,0.65); font-size: 15px; margin-bottom: 14px; }
        .aud-hero-badges { display: flex; gap: 8px; flex-wrap: wrap; }
        .aud-hero-badge {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 5px 12px; border-radius: 20px; font-size: 12px; font-weight: 600;
        }
        .aud-hero-badge.id { background: rgba(255,255,255,0.10); color: rgba(255,255,255,0.90); }
        .aud-hero-badge.admin { background: rgba(255,153,0,0.20); color: #fdba74; border: 1px solid rgba(255,153,0,0.40); }
        .aud-hero-badge.active { background: rgba(56,161,105,0.20); color: #86efac; }
        .aud-hero-badge.inactive { background: rgba(229,62,62,0.20); color: #fca5a5; }
        .aud-hero-badge.locked { background: rgba(229,62,62,0.30); color: #fef2f2; animation: aud-blink 1.5s infinite; }
        @keyframes aud-blink { 0%,100% { opacity: 1; } 50% { opacity: 0.6; } }

        .aud-hero-actions { display: flex; flex-direction: column; gap: 10px; position: relative; z-index: 1; }
        .aud-hero-btn {
          padding: 10px 18px; border-radius: 12px; border: none;
          font-family: 'Lexend', sans-serif; font-weight: 600; font-size: 13px;
          cursor: pointer; display: inline-flex; align-items: center; gap: 8px;
          transition: all 0.2s; white-space: nowrap;
        }
        .aud-hero-btn.add { background: linear-gradient(135deg, #38a169, #2f855a); color: #fff; }
        .aud-hero-btn.add:hover { box-shadow: 0 6px 20px rgba(56,161,105,0.40); transform: translateY(-2px); }
        .aud-hero-btn.remove { background: linear-gradient(135deg, #e53e3e, #c53030); color: #fff; }
        .aud-hero-btn.remove:hover { box-shadow: 0 6px 20px rgba(229,62,62,0.40); transform: translateY(-2px); }

        /* Summary stats */
        .aud-summary { display: grid; grid-template-columns: repeat(6, 1fr); gap: 12px; margin-bottom: 24px; }
        .aud-stat {
          background: #fff; padding: 18px; border-radius: 14px;
          box-shadow: 0 4px 16px rgba(0,0,0,0.05); border: 1px solid rgba(0,0,0,0.04);
          text-align: center;
        }
        .aud-stat-icon { width: 36px; height: 36px; border-radius: 50%; margin: 0 auto 8px; display: flex; align-items: center; justify-content: center; font-size: 14px; }
        .aud-stat-icon.orange { background: rgba(255,153,0,0.15); color: #f90; }
        .aud-stat-icon.green { background: rgba(56,161,105,0.15); color: #2f855a; }
        .aud-stat-icon.red { background: rgba(229,62,62,0.15); color: #c53030; }
        .aud-stat-icon.blue { background: rgba(59,130,246,0.15); color: #2563eb; }
        .aud-stat-icon.purple { background: rgba(168,85,247,0.15); color: #a855f7; }
        .aud-stat-value { font-size: 22px; font-weight: 700; color: #333; }
        .aud-stat-label { font-size: 11px; color: #999; text-transform: uppercase; letter-spacing: 0.5px; margin-top: 4px; }

        /* Tabs */
        .aud-tabs {
          background: #fff; border-radius: 14px 14px 0 0;
          padding: 0 16px; display: flex; gap: 4px; border-bottom: 1px solid #eee;
          box-shadow: 0 4px 16px rgba(0,0,0,0.04);
        }
        .aud-tab {
          padding: 14px 20px; border: none; background: transparent; cursor: pointer;
          font-family: 'Lexend', sans-serif; font-size: 13px; font-weight: 600; color: #888;
          border-bottom: 3px solid transparent; transition: all 0.2s;
          display: inline-flex; align-items: center; gap: 8px;
        }
        .aud-tab:hover { color: #f90; }
        .aud-tab.active { color: #f90; border-bottom-color: #f90; }
        .aud-tab-count {
          display: inline-flex; align-items: center; justify-content: center;
          min-width: 22px; height: 22px; padding: 0 7px;
          border-radius: 11px; font-size: 11px; font-weight: 700;
          background: #f0f0f0; color: #666;
        }
        .aud-tab.active .aud-tab-count { background: #f90; color: #fff; }

        /* Content area */
        .aud-content {
          background: #fff; border-radius: 0 0 14px 14px; padding: 0;
          box-shadow: 0 4px 20px rgba(0,0,0,0.05); border: 1px solid #eee; border-top: 0;
          margin-bottom: 30px;
        }

        /* Info tab */
        .aud-info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; padding: 24px; }
        .aud-info-block {
          background: #fafafa; padding: 14px 16px; border-radius: 10px;
          border-left: 3px solid #f90;
        }
        .aud-info-block h4 { font-size: 11px; color: #888; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 6px; }
        .aud-info-block p { font-size: 14px; color: #333; font-weight: 500; word-break: break-all; }
        .aud-info-block.alert { border-left-color: #e53e3e; background: rgba(229,62,62,0.04); }
        .aud-info-block.alert p { color: #c53030; }

        /* Tables */
        .aud-table { width: 100%; border-collapse: collapse; }
        .aud-table th {
          text-align: left; padding: 12px 20px; font-size: 11px; color: #888;
          font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px;
          background: #fafafa; border-bottom: 1px solid #eee;
        }
        .aud-table td { padding: 12px 20px; font-size: 13px; color: #444; border-bottom: 1px solid #f0f0f0; }
        .aud-table tr:last-child td { border-bottom: none; }
        .aud-table tr:hover { background: #fafafa; }

        .aud-mono { font-family: 'SF Mono', Consolas, monospace; font-size: 12px; }

        .aud-pill { display: inline-flex; align-items: center; gap: 4px; padding: 3px 9px; border-radius: 10px; font-size: 11px; font-weight: 600; }
        .aud-pill.approved { background: rgba(56,161,105,0.15); color: #2f855a; }
        .aud-pill.pending { background: rgba(237,137,54,0.15); color: #d97706; }
        .aud-pill.cancelled { background: rgba(229,62,62,0.15); color: #c53030; }
        .aud-pill.manual { background: rgba(168,85,247,0.15); color: #a855f7; }
        .aud-pill.manual_debit { background: rgba(220,38,38,0.15); color: #dc2626; }
        .aud-pill.success { background: rgba(56,161,105,0.15); color: #2f855a; }
        .aud-pill.error { background: rgba(229,62,62,0.15); color: #c53030; }

        .aud-empty {
          padding: 60px 20px; text-align: center; color: #999;
        }
        .aud-empty i { font-size: 48px; color: #ddd; display: block; margin-bottom: 12px; }
        .aud-empty p { font-size: 14px; }

        /* Modal */
        .aud-modal-overlay {
          position: fixed; inset: 0; background: rgba(0,0,0,0.5); backdrop-filter: blur(4px);
          display: flex; align-items: center; justify-content: center; z-index: 2000; padding: 20px;
        }
        .aud-modal {
          background: #fff; border-radius: 20px; padding: 30px; width: 100%; max-width: 420px;
          box-shadow: 0 20px 60px rgba(0,0,0,0.30); position: relative;
        }
        .aud-modal-close {
          position: absolute; top: 14px; right: 14px; background: none; border: none;
          font-size: 18px; color: #999; cursor: pointer; width: 32px; height: 32px;
          border-radius: 50%; display: flex; align-items: center; justify-content: center;
        }
        .aud-modal-close:hover { background: #f0f0f0; color: #444; }
        .aud-modal-title { font-size: 20px; font-weight: 700; color: #222; margin-bottom: 20px; }
        .aud-modal-tabs { display: flex; gap: 6px; margin-bottom: 18px; padding: 4px; background: #f5f5f5; border-radius: 12px; }
        .aud-modal-tab {
          flex: 1; padding: 10px; border-radius: 8px; border: none; background: transparent;
          font-family: 'Lexend', sans-serif; font-size: 13px; font-weight: 600; color: #666; cursor: pointer;
        }
        .aud-modal-tab.active.add { background: #38a169; color: #fff; }
        .aud-modal-tab.active.remove { background: #e53e3e; color: #fff; }
        .aud-modal-info {
          background: rgba(255,153,0,0.06); border: 1px solid rgba(255,153,0,0.18);
          padding: 12px 14px; border-radius: 10px; margin-bottom: 16px;
          font-size: 13px; color: #444;
        }
        .aud-modal-info strong { color: #f90; }
        .aud-modal-input {
          width: 100%; padding: 12px 16px; border: 2px solid #e0e0e0; border-radius: 10px;
          font-family: 'Lexend', sans-serif; font-size: 14px; outline: none; margin-bottom: 14px;
        }
        .aud-modal-input:focus { border-color: #f90; box-shadow: 0 0 0 3px rgba(255,153,0,0.15); }
        .aud-modal-label { display: block; font-size: 12px; font-weight: 600; color: #666; margin-bottom: 6px; }
        .aud-modal-submit {
          width: 100%; padding: 14px; border-radius: 12px; border: none; cursor: pointer;
          font-family: 'Lexend', sans-serif; font-weight: 700; font-size: 14px; color: #fff;
          margin-top: 6px; transition: all 0.2s;
        }
        .aud-modal-submit:disabled { opacity: 0.5; cursor: not-allowed; }
        .aud-alert { padding: 10px 14px; border-radius: 10px; font-size: 13px; margin-bottom: 14px; }
        .aud-alert.success { background: rgba(56,161,105,0.10); color: #2f855a; border-left: 3px solid #38a169; }
        .aud-alert.error { background: rgba(229,62,62,0.10); color: #c53030; border-left: 3px solid #e53e3e; }

        @media (max-width: 1280px) {
          .aud-summary { grid-template-columns: repeat(3, 1fr); }
          .aud-info-grid { grid-template-columns: 1fr; }
        }
        @media (max-width: 640px) {
          .aud-hero { flex-direction: column; text-align: center; }
          .aud-hero-actions { width: 100%; }
          .aud-summary { grid-template-columns: repeat(2, 1fr); }
          .aud-tabs { overflow-x: auto; }
          .aud-table th, .aud-table td { padding: 10px 14px; font-size: 12px; }
        }
      `}</style>

      <div className="aud">
        <header className="aud-header">
          <h1 className="aud-header-title">Detalhes do Usuário</h1>
          <div className="aud-header-back" onClick={fetchDetails} title="Atualizar">
            <i className="fas fa-sync-alt"></i> Atualizar
          </div>
          {/* Botão "Voltar" removido — navegação já fica na sidebar (AdminLayout) */}
        </header>

        <main className="aud-main">
          {/* Hero */}
          <div className="aud-hero">
            <div className="aud-hero-avatar">{(user.username || '?').slice(0, 1).toUpperCase()}</div>
            <div className="aud-hero-info">
              <h1>{user.username}</h1>
              <div className="aud-hero-email"><i className="fas fa-envelope" style={{ marginRight: 6 }}></i>{user.email}</div>
              <div className="aud-hero-badges">
                <span className="aud-hero-badge id">#{user.id}</span>
                {user.is_admin && <span className="aud-hero-badge admin"><i className="fas fa-shield-alt"></i> Admin</span>}
                {user.is_locked
                  ? <span className="aud-hero-badge locked"><i className="fas fa-lock"></i> Bloqueado</span>
                  : user.is_active
                    ? <span className="aud-hero-badge active"><i className="fas fa-check-circle"></i> Ativo</span>
                    : <span className="aud-hero-badge inactive"><i className="fas fa-times-circle"></i> Inativo</span>
                }
              </div>
            </div>
            <div className="aud-hero-actions">
              <button className="aud-hero-btn add" onClick={() => openAdjust('add')}>
                <i className="fas fa-plus"></i> Adicionar créditos
              </button>
              <button className="aud-hero-btn remove" onClick={() => openAdjust('remove')}>
                <i className="fas fa-minus"></i> Remover créditos
              </button>
            </div>
          </div>

          {/* Summary cards */}
          <div className="aud-summary">
            <div className="aud-stat">
              <div className="aud-stat-icon orange"><i className="fas fa-coins"></i></div>
              <div className="aud-stat-value">{user.credits}</div>
              <div className="aud-stat-label">Saldo atual</div>
            </div>
            <div className="aud-stat">
              <div className="aud-stat-icon green"><i className="fas fa-credit-card"></i></div>
              <div className="aud-stat-value">{summary.credits_purchased}</div>
              <div className="aud-stat-label">Comprados</div>
            </div>
            <div className="aud-stat">
              <div className="aud-stat-icon purple"><i className="fas fa-plus-circle"></i></div>
              <div className="aud-stat-value">{summary.credits_added_manual}</div>
              <div className="aud-stat-label">Add. manual</div>
            </div>
            <div className="aud-stat">
              <div className="aud-stat-icon red"><i className="fas fa-minus-circle"></i></div>
              <div className="aud-stat-value">{summary.credits_removed_manual}</div>
              <div className="aud-stat-label">Removidos</div>
            </div>
            <div className="aud-stat">
              <div className="aud-stat-icon blue"><i className="fas fa-qrcode"></i></div>
              <div className="aud-stat-value">{summary.total_codes}</div>
              <div className="aud-stat-label">Códigos</div>
            </div>
            <div className="aud-stat">
              <div className="aud-stat-icon green"><i className="fas fa-sign-in-alt"></i></div>
              <div className="aud-stat-value">{summary.total_logins}</div>
              <div className="aud-stat-label">Logins</div>
            </div>
          </div>

          {/* Tabs */}
          <div className="aud-tabs">
            {[
              { k: 'info',     l: 'Informações',  i: 'fa-id-card', c: null },
              { k: 'codes',    l: 'Códigos',      i: 'fa-qrcode',  c: totalCodes },
              { k: 'credits',  l: 'Créditos',     i: 'fa-coins',   c: orders.length },
              { k: 'logins',   l: 'Logins',       i: 'fa-key',     c: logins.length },
              { k: 'activity', l: 'Atividade',    i: 'fa-history', c: activity.length },
            ].map(t => (
              <button
                key={t.k}
                className={`aud-tab ${tab === t.k ? 'active' : ''}`}
                onClick={() => setTab(t.k)}
              >
                <i className={`fas ${t.i}`}></i>
                {t.l}
                {t.c !== null && <span className="aud-tab-count">{t.c}</span>}
              </button>
            ))}
          </div>

          <div className="aud-content">
            {/* Info */}
            {tab === 'info' && (
              <div className="aud-info-grid">
                <div className="aud-info-block">
                  <h4>ID</h4>
                  <p>#{user.id}</p>
                </div>
                <div className="aud-info-block">
                  <h4>Nome de usuário</h4>
                  <p>{user.username}</p>
                </div>
                <div className="aud-info-block">
                  <h4>Email</h4>
                  <p>{user.email}</p>
                </div>
                <div className="aud-info-block">
                  <h4>Cadastrado em</h4>
                  <p>{formatDateTime(user.created_at)}</p>
                </div>
                <div className="aud-info-block">
                  <h4>Última atualização</h4>
                  <p>{formatDateTime(user.updated_at)}</p>
                </div>
                <div className="aud-info-block">
                  <h4>Último login</h4>
                  <p>{user.last_login ? formatDateTime(user.last_login) : '— nunca'}</p>
                </div>
                {user.failed_login_attempts > 0 && (
                  <div className="aud-info-block alert">
                    <h4>Tentativas de login falhas</h4>
                    <p>{user.failed_login_attempts} tentativa(s)</p>
                  </div>
                )}
                {user.is_locked && (
                  <div className="aud-info-block alert">
                    <h4>Bloqueado até</h4>
                    <p>{formatDateTime(user.locked_until)}</p>
                  </div>
                )}
              </div>
            )}

            {/* Códigos */}
            {tab === 'codes' && (
              codes.length === 0 ? (
                <div className="aud-empty"><i className="fas fa-qrcode"></i><p>Nenhum código gerado</p></div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table className="aud-table tbl-codes">
                    <thead>
                      <tr>
                        <th>Código</th>
                        <th>Origem</th>
                        <th>Destino</th>
                        <th>Lote</th>
                        <th>Criado em</th>
                      </tr>
                    </thead>
                    <tbody>
                      {codes.map((c, i) => (
                        <tr key={i}>
                          <td className="aud-mono"><strong>{c.codigo}</strong></td>
                          <td>{c.origem || '—'}</td>
                          <td>{c.destino || '—'}</td>
                          <td className="aud-mono" style={{ color: '#888' }}>{c.lote_id || '—'}</td>
                          <td>{formatDateTime(c.data_criacao || c.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {totalCodes > codes.length && (
                    <div style={{ padding: 14, textAlign: 'center', fontSize: 12, color: '#888' }}>
                      Mostrando últimos {codes.length} de {totalCodes}
                    </div>
                  )}
                </div>
              )
            )}

            {/* Créditos */}
            {tab === 'credits' && (
              orders.length === 0 ? (
                <div className="aud-empty"><i className="fas fa-coins"></i><p>Nenhuma movimentação de créditos</p></div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table className="aud-table tbl-orders">
                    <thead>
                      <tr>
                        <th>#ID</th>
                        <th>Quantidade</th>
                        <th>Status</th>
                        <th>Criado em</th>
                        <th>Atualizado em</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.map(o => (
                        <tr key={o.id}>
                          <td>#{o.id}</td>
                          <td><strong>{o.quantity}</strong></td>
                          <td>
                            <span className={`aud-pill ${o.status}`}>
                              {o.status === 'approved' ? 'Aprovado' :
                               o.status === 'pending' ? 'Pendente' :
                               o.status === 'cancelled' ? 'Cancelado' :
                               o.status === 'manual' ? 'Adicionado pelo Admin' :
                               o.status === 'manual_debit' ? 'Removido pelo Admin' : o.status}
                            </span>
                          </td>
                          <td>{formatDateTime(o.created_at)}</td>
                          <td>{formatDateTime(o.updated_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            )}

            {/* Logins */}
            {tab === 'logins' && (
              logins.length === 0 ? (
                <div className="aud-empty"><i className="fas fa-key"></i><p>Nenhum registro de login</p></div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table className="aud-table tbl-sessions">
                    <thead>
                      <tr>
                        <th>Quando</th>
                        <th>Tipo</th>
                        <th>IP</th>
                        <th>User-Agent</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logins.map(l => (
                        <tr key={l.id}>
                          <td>
                            {formatDateTime(l.created_at)}
                            <div style={{ fontSize: 11, color: '#999' }}>{formatRel(l.created_at)}</div>
                          </td>
                          <td>
                            {l.action === 'LOGIN_FAILED' && <span className="aud-pill error"><i className="fas fa-times"></i> Falhou</span>}
                            {(l.action === 'USER_LOGIN' || l.action === 'LOGIN_SUCCESS') && <span className="aud-pill success"><i className="fas fa-check"></i> Sucesso</span>}
                            {l.action === 'ACCOUNT_LOCKED' && <span className="aud-pill error"><i className="fas fa-lock"></i> Bloqueado</span>}
                          </td>
                          <td className="aud-mono" style={{ color: '#666' }}>{l.ip_address || '—'}</td>
                          <td className="aud-mono" style={{ color: '#999', fontSize: 11, maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={l.user_agent}>
                            {l.user_agent || '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            )}

            {/* Atividade geral */}
            {tab === 'activity' && (
              activity.length === 0 ? (
                <div className="aud-empty"><i className="fas fa-history"></i><p>Sem atividade registrada</p></div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table className="aud-table tbl-activity">
                    <thead>
                      <tr>
                        <th>Quando</th>
                        <th>Ação</th>
                        <th>Status</th>
                        <th>IP</th>
                        <th>Detalhes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activity.map(a => (
                        <tr key={a.id}>
                          <td>
                            {formatDateTime(a.created_at)}
                            <div style={{ fontSize: 11, color: '#999' }}>{formatRel(a.created_at)}</div>
                          </td>
                          <td className="aud-mono" style={{ fontWeight: 600 }}>{a.action}</td>
                          <td>
                            <span className={`aud-pill ${a.status === 'error' ? 'error' : 'success'}`}>
                              {a.status === 'error' ? 'Erro' : 'OK'}
                            </span>
                          </td>
                          <td className="aud-mono" style={{ color: '#666' }}>{a.ip_address || '—'}</td>
                          <td className="aud-mono" style={{ color: '#888', fontSize: 11, maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {a.details ? (typeof a.details === 'string' ? a.details : JSON.stringify(a.details)) : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            )}
          </div>
        </main>
      </div>

      {/* Adjust Credits Modal */}
      {adjustOpen && (
        <div className="aud-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setAdjustOpen(false); }}>
          <div className="aud-modal">
            <button className="aud-modal-close" onClick={() => setAdjustOpen(false)}>✕</button>
            <h3 className="aud-modal-title">Ajustar créditos</h3>

            <div className="aud-modal-tabs">
              <button className={`aud-modal-tab ${adjustMode === 'add' ? 'active add' : ''}`} onClick={() => setAdjustMode('add')}>
                <i className="fas fa-plus-circle"></i> Adicionar
              </button>
              <button className={`aud-modal-tab ${adjustMode === 'remove' ? 'active remove' : ''}`} onClick={() => setAdjustMode('remove')}>
                <i className="fas fa-minus-circle"></i> Remover
              </button>
            </div>

            <div className="aud-modal-info">
              <strong>{user.username}</strong> · saldo atual: <strong>{user.credits}</strong> créditos
            </div>

            {adjustSuccess && <div className="aud-alert success">{adjustSuccess}</div>}
            {adjustError && <div className="aud-alert error">{adjustError}</div>}

            <label className="aud-modal-label">Quantidade {adjustMode === 'add' ? 'a adicionar' : 'a remover'}</label>
            <input
              type="number" min="1" max={adjustMode === 'remove' ? user.credits : 100000}
              className="aud-modal-input"
              value={adjustQty}
              onChange={(e) => setAdjustQty(e.target.value)}
              placeholder="Ex: 100"
              autoFocus
            />

            <label className="aud-modal-label">Motivo (opcional)</label>
            <input
              type="text" maxLength={200}
              className="aud-modal-input"
              value={adjustReason}
              onChange={(e) => setAdjustReason(e.target.value)}
              placeholder="Ex: Cortesia, ajuste, reembolso..."
            />

            <button
              onClick={submitAdjust}
              disabled={adjustLoading}
              className="aud-modal-submit"
              style={{
                background: adjustMode === 'add' ? 'linear-gradient(135deg, #38a169, #2f855a)' : 'linear-gradient(135deg, #e53e3e, #c53030)',
              }}
            >
              {adjustLoading ? 'Processando...' : (adjustMode === 'add' ? `Adicionar ${adjustQty || ''} créditos` : `Remover ${adjustQty || ''} créditos`)}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminUserDetail;
